import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Landmark,
  Download,
  Trash2,
  FileText,
  RefreshCw,
  Church,
  Mail,
  Send,
  Upload,
  Users,
  AlertTriangle,
  ExternalLink,
  FileSignature,
  ClipboardList,
  HandCoins,
  BarChart3,
  Plus,
  Target,
  X,
} from "lucide-react";
import api, { formatApiError, API } from "../../lib/api";
import MilestoneLetterCard from "./MilestoneLetterCard";
import MilestoneEmailCard from "./MilestoneEmailCard";

/**
 * Admin panel for the Proposed Trillion Dollar Spending Power FCU digital-membership
 * funnel. Lists every subscriber captured on `/credit-union`, offers a
 * one-click NCUA CSV export, per-member PDF card re-download, and a delete
 * (for testing / typos).
 */
export default function CreditUnionTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        api.get("/admin/credit-union/subscribers"),
        api.get("/credit-union/stats"),
      ]);
      setItems(subsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const downloadCsv = () => {
    const token = localStorage.getItem("bdu_token");
    // Direct link + Bearer via fetch, then trigger download blob (server sets Content-Disposition)
    fetch(`${API}/admin/credit-union/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const ts = new Date().toISOString().replace(/[-:.]/g, "").slice(0, 15);
        a.href = url;
        a.download = `tdspfcu-charter-supporters-${ts}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Downloaded ${items.length} charter-supporter row(s)`);
      })
      .catch((e) => toast.error(`CSV export failed: ${e.message}`));
  };

  const downloadCard = (id, memberNumber) => {
    const token = localStorage.getItem("bdu_token");
    fetch(`${API}/admin/credit-union/subscribers/${id}/card.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${memberNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(`PDF failed: ${e.message}`));
  };

  const deleteSubscriber = async (id, memberNumber) => {
    if (!window.confirm(`Delete ${memberNumber}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/credit-union/subscribers/${id}`);
      toast.success(`${memberNumber} removed`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const totalPaid = items.filter((i) => i.payment_status === "paid").length;

  return (
    <div className="space-y-8" data-testid="admin-cu-tab">
      <MilestoneLetterCard />
      <MilestoneEmailCard />
      {/* Header + top actions */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Landmark size={12} className="text-bdu-gold" /> Proposed Trillion Dollar Spending Power FCU
          </div>
          <h3 className="mt-2 font-serif text-2xl text-bdu-cream">
            Charter-Supporter Roster
          </h3>
          <p className="mt-1 text-xs text-bdu-cream/50 max-w-xl">
            Every charter-support pledge captured on <code>/credit-union</code>.
            Export the roster as CSV for NCUA charter-application records.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={load}
            className="btn-ghost text-xs flex items-center gap-2"
            data-testid="admin-cu-refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={downloadCsv}
            disabled={items.length === 0}
            className="btn-gold text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-cu-export-btn"
          >
            <Download size={14} /> Export CSV ({items.length})
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      {stats && (
        <>
          <div className="grid sm:grid-cols-4 gap-4" data-testid="admin-cu-stats">
            <StatTile
              label="Charter Supporters"
              value={stats.count}
              sub={`of ${stats.goal_members.toLocaleString()}`}
              testId="admin-cu-stat-count"
            />
            <StatTile
              label="Pledged"
              value={`$${Number(stats.total_pledged || 0).toLocaleString()}`}
              sub="Aggregate charter-support pledges"
              testId="admin-cu-stat-pledged"
              gold
            />
            <StatTile
              label="Paid Now"
              value={totalPaid}
              sub="Zeffy-completed"
              testId="admin-cu-stat-paid"
            />
            <StatTile
              label="Supporter Slots Remaining"
              value={Number(stats.seats_remaining || 0).toLocaleString()}
              sub={`${(stats.progress_pct || 0).toFixed(2)}% to charter goal`}
              testId="admin-cu-stat-remaining"
            />
          </div>

          {/* FOM Compliance tiles — critical input for the NCUA Field-of-Membership Study */}
          <div className="mt-6" data-testid="admin-cu-fom">
            <div className="label-eyebrow flex items-center gap-2 mb-3">
              <Landmark size={12} className="text-bdu-gold" />
              Field of Membership · Fulton · DeKalb
            </div>
            <div className="grid sm:grid-cols-4 gap-4">
              <StatTile
                label="In-FOM Compliance"
                value={`${(stats.fom_compliance_pct || 0).toFixed(1)}%`}
                sub={`${stats.in_fom_count} of ${stats.count} pledges`}
                testId="admin-cu-stat-fom-pct"
                gold
              />
              <StatTile
                label="Fulton"
                value={stats.by_county?.Fulton?.count || 0}
                sub={`$${Number(stats.by_county?.Fulton?.pledged || 0).toLocaleString()}`}
                testId="admin-cu-stat-fulton"
              />
              <StatTile
                label="DeKalb"
                value={stats.by_county?.DeKalb?.count || 0}
                sub={`$${Number(stats.by_county?.DeKalb?.pledged || 0).toLocaleString()}`}
                testId="admin-cu-stat-dekalb"
              />
              <StatTile
                label="Out-of-FOM"
                value={stats.by_county?.["Out-of-FOM"]?.count || 0}
                sub={`$${Number(stats.by_county?.["Out-of-FOM"]?.pledged || 0).toLocaleString()}`}
                testId="admin-cu-stat-outoffom"
              />
            </div>
          </div>

          {/* Documented Demand — paper + inline surveys toward NCUA 1/2.5/5% benchmark */}
          <DocumentedDemandTiles />

          {/* NCUA FOM Submission Packet — one-click download bundle */}
          <FOMPacketCard />

          {/* Charter Forms Vault — board / organizing committee kit */}
          <FormsVaultCard />
        </>
      )}

      {/* ONE-CLICK OUTREACH — email the Church Pack PDF to a partner pastor */}
      <ChurchPackOutreachCard />

      {loading ? (
        <div className="text-bdu-cream/50 py-14 text-center">Loading supporters…</div>
      ) : items.length === 0 ? (
        <div className="border border-bdu-gold/10 py-16 text-center text-bdu-cream/50">
          No supporters yet. Share <code className="text-bdu-gold">/credit-union</code> to
          get the charter-supporter roster started.
        </div>
      ) : (
        <div className="border border-bdu-gold/10 overflow-x-auto" data-testid="admin-cu-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-bdu-surface2 text-bdu-cream/60 uppercase tracking-widest text-xs">
              <tr>
                <th className="px-4 py-3">Supporter ID</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">FOM</th>
                <th className="px-4 py-3">Pledge</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Signed</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr
                  key={m.id}
                  className="border-t border-bdu-gold/10"
                  data-testid={`admin-cu-row-${m.member_number}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-bdu-gold whitespace-nowrap">
                    {m.member_number}
                  </td>
                  <td className="px-4 py-3 text-bdu-cream whitespace-nowrap">{m.full_name}</td>
                  <td className="px-4 py-3 text-bdu-cream/80">{m.email}</td>
                  <td className="px-4 py-3 text-bdu-cream/70 whitespace-nowrap">
                    {m.city}, {m.state} {m.zip_code}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {m.in_fom ? (
                      <span className="text-[0.6rem] uppercase tracking-widest px-2 py-1 border border-emerald-500/40 text-emerald-400">
                        {m.fom_county || "In"}
                      </span>
                    ) : (
                      <span className="text-[0.6rem] uppercase tracking-widest px-2 py-1 border border-bdu-gold/40 text-bdu-gold">
                        Out
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-bdu-gold font-serif whitespace-nowrap">
                    ${Number(m.pledge_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={m.payment_status} payNow={m.pay_now_selected} />
                  </td>
                  <td className="px-4 py-3 text-bdu-cream/60 whitespace-nowrap">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => downloadCard(m.id, m.member_number)}
                      className="text-bdu-gold hover:text-bdu-cream inline-flex items-center gap-1 mr-3 text-xs uppercase tracking-widest"
                      data-testid={`admin-cu-card-${m.member_number}`}
                      title="Re-download charter-supporter PDF card"
                    >
                      <FileText size={13} /> Card
                    </button>
                    <button
                      onClick={() => deleteSubscriber(m.id, m.member_number)}
                      className="text-bdu-red/80 hover:text-bdu-red inline-flex items-center gap-1 text-xs uppercase tracking-widest"
                      data-testid={`admin-cu-delete-${m.member_number}`}
                    >
                      <Trash2 size={13} /> Del
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, sub, gold = false, testId }) {
  return (
    <div className="border border-bdu-gold/20 bg-bdu-surface p-5" data-testid={testId}>
      <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>
        {label}
      </div>
      <div
        className={`mt-3 font-serif text-3xl tabular-nums ${gold ? "text-bdu-gold" : "text-bdu-cream"}`}
      >
        {value}
      </div>
      <div className="mt-1 text-xs text-bdu-cream/50">{sub}</div>
    </div>
  );
}

function StatusPill({ status, payNow }) {
  const map = {
    paid: "border-emerald-500/40 text-emerald-400",
    pending: "border-bdu-gold/40 text-bdu-gold",
    committed: "border-bdu-cream/30 text-bdu-cream/80",
    refunded: "border-bdu-red/40 text-bdu-red",
  };
  const label =
    status === "pending" && payNow
      ? "AWAITING PAY"
      : (status || "committed").toUpperCase();
  return (
    <span
      className={`text-[0.6rem] uppercase tracking-widest px-2 py-1 border ${map[status] || map.committed}`}
    >
      {label}
    </span>
  );
}



// ---------- Church Pack Outreach Card ----------

const CHURCH_PACK_PUBLIC_URL = `${API}/credit-union/outreach/church-pack.pdf`;

function ChurchPackOutreachCard() {
  const [form, setForm] = useState({ to: "", pastor_name: "", church_name: "", note: "" });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/credit-union/outreach/history", { params: { limit: 25 } });
      setHistory(r.data?.rows || []);
    } catch (_e) {
      /* silent */
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const downloadPdf = () => {
    const a = document.createElement("a");
    a.href = CHURCH_PACK_PUBLIC_URL;
    a.download = "BDU_Church_Outreach_Kit.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Church Outreach Kit — downloading");
  };

  const previewPdf = () => {
    window.open(CHURCH_PACK_PUBLIC_URL + "?inline=true", "_blank", "noopener,noreferrer");
  };

  const sendToPastor = async (e) => {
    e?.preventDefault?.();
    const to = form.to.trim();
    if (!to || !/^\S+@\S+\.\S+$/.test(to)) {
      toast.error("Please enter the pastor's email");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/credit-union/outreach/send-church-pack", {
        to,
        pastor_name: form.pastor_name.trim() || null,
        church_name: form.church_name.trim() || null,
        note: form.note.trim() || null,
      });
      const kb = Math.round((r.data?.attachment_bytes || 0) / 1024);
      toast.success(
        form.church_name.trim()
          ? `Church Pack queued to ${form.church_name.trim()} (${kb} KB attached)`
          : `Church Pack queued to ${to} (${kb} KB attached)`
      );
      setForm({ to: "", pastor_name: "", church_name: "", note: "" });
      // let the background task flush, then refresh history
      setTimeout(loadHistory, 900);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="border border-bdu-gold/25 bg-black/50 p-6 sm:p-8"
      data-testid="admin-cu-church-pack-card"
      style={{
        boxShadow:
          "0 30px 60px -30px rgba(212,175,55,0.3), inset 0 1px 0 rgba(212,175,55,0.06)",
      }}
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center border border-bdu-gold/40 bg-bdu-gold/5">
            <Church size={18} className="text-bdu-gold" />
          </div>
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <FileText size={11} className="text-bdu-gold" /> Church Outreach Kit · 7-page PDF
            </div>
            <h3 className="mt-1 font-serif text-xl text-bdu-cream">
              Email the Church Pack to a partner pastor
            </h3>
            <p className="mt-1 text-xs text-bdu-cream/55 max-w-lg leading-relaxed">
              One click. The 7-page print-ready PDF is attached to a warm,
              NCUA-compliant email from <span className="text-bdu-cream">Black Dollars United</span>.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={downloadPdf}
            className="btn-ghost text-xs flex items-center gap-2"
            data-testid="admin-cu-church-pack-download-btn"
          >
            <Download size={14} /> Download PDF
          </button>
          <button
            type="button"
            onClick={previewPdf}
            className="btn-ghost text-xs flex items-center gap-2"
            data-testid="admin-cu-church-pack-preview-btn"
          >
            <FileText size={14} /> Preview
          </button>
        </div>
      </div>

      {/* Send form */}
      <form onSubmit={sendToPastor} className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="admin-cu-church-pack-form">
        <label className="text-xs text-bdu-cream/60 flex flex-col gap-1">
          Pastor's email <span className="text-bdu-red">*</span>
          <input
            type="email"
            value={form.to}
            onChange={setField("to")}
            placeholder="pastor@firstbaptist.org"
            required
            disabled={sending}
            data-testid="admin-cu-church-pack-input-to"
            className="bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none transition disabled:opacity-50"
          />
        </label>
        <label className="text-xs text-bdu-cream/60 flex flex-col gap-1">
          Pastor's name (optional)
          <input
            type="text"
            value={form.pastor_name}
            onChange={setField("pastor_name")}
            placeholder="Rev. Dr. ..."
            disabled={sending}
            data-testid="admin-cu-church-pack-input-name"
            className="bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none transition disabled:opacity-50"
          />
        </label>
        <label className="text-xs text-bdu-cream/60 flex flex-col gap-1 sm:col-span-2">
          Church name (optional)
          <input
            type="text"
            value={form.church_name}
            onChange={setField("church_name")}
            placeholder="First Baptist Church of Atlanta"
            disabled={sending}
            data-testid="admin-cu-church-pack-input-church"
            className="bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none transition disabled:opacity-50"
          />
        </label>
        <label className="text-xs text-bdu-cream/60 flex flex-col gap-1 sm:col-span-2">
          Personal note (optional, appears above the kit description)
          <textarea
            value={form.note}
            onChange={setField("note")}
            placeholder="Pastor, following up on our conversation after Sunday service..."
            rows={3}
            maxLength={800}
            disabled={sending}
            data-testid="admin-cu-church-pack-input-note"
            className="bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none transition resize-y disabled:opacity-50"
          />
        </label>
        <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-3">
          <p className="text-[11px] text-bdu-cream/40 italic">
            Email sent via Resend. The PDF (~25 KB) is attached — no login required for the pastor.
          </p>
          <button
            type="submit"
            disabled={sending || !form.to.trim()}
            className="btn-gold text-xs flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-cu-church-pack-send-btn"
          >
            {sending ? (
              <>
                <RefreshCw size={14} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send size={14} /> Send Church Pack
              </>
            )}
          </button>
        </div>
      </form>

      {/* Bulk CSV blast */}
      <ChurchPackBulkBlast
        onComplete={() => setTimeout(loadHistory, 900)}
      />

      {/* History */}
      <div className="mt-6 pt-5 border-t border-bdu-gold/15">
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="text-xs text-bdu-cream/60 hover:text-bdu-gold flex items-center gap-2 transition-colors"
          data-testid="admin-cu-church-pack-history-toggle"
        >
          <Mail size={12} />
          {showHistory ? "Hide" : "Show"} recent sends ({history.length})
        </button>
        {showHistory && (
          <div className="mt-4" data-testid="admin-cu-church-pack-history">
            {history.length === 0 ? (
              <div className="text-xs text-bdu-cream/40 italic">No sends yet.</div>
            ) : (
              <ul className="space-y-2 max-h-56 overflow-auto pr-2">
                {history.map((h, i) => (
                  <li
                    key={`${h.to}-${h.sent_at}-${i}`}
                    className="flex items-start justify-between gap-4 text-xs border-b border-bdu-gold/10 pb-2"
                  >
                    <div className="min-w-0">
                      <div className="text-bdu-cream truncate">
                        {h.church_name || h.pastor_name || h.to}
                      </div>
                      <div className="text-bdu-cream/50 truncate">{h.to}</div>
                    </div>
                    <div className="text-bdu-cream/40 flex-shrink-0 whitespace-nowrap">
                      {h.sent_at ? new Date(h.sent_at).toLocaleString() : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------- Church Pack · Bulk CSV Blast ----------

const BULK_SAMPLE_URL = `${API}/admin/credit-union/outreach/sample.csv`;

function ChurchPackBulkBlast({ onComplete }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { valid: n, invalid: n, duplicate: n }
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const parseLocal = async (f) => {
    try {
      const text = await f.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length === 0) {
        setPreview({ valid: 0, invalid: 0, duplicate: 0, missing_header: true });
        return;
      }
      const headerLower = lines[0].toLowerCase();
      if (!headerLower.includes("email")) {
        setPreview({ valid: 0, invalid: 0, duplicate: 0, missing_header: true });
        return;
      }
      // crude CSV split — only need to count the email column for preview
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const emailIdx = headers.indexOf("email");
      const seen = new Set();
      let valid = 0;
      let invalid = 0;
      let duplicate = 0;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",");
        const em = (cols[emailIdx] || "").trim().toLowerCase();
        if (!em || !re.test(em)) {
          invalid += 1;
          continue;
        }
        if (seen.has(em)) {
          duplicate += 1;
          continue;
        }
        seen.add(em);
        valid += 1;
      }
      setPreview({ valid, invalid, duplicate });
    } catch (_e) {
      setPreview(null);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setResult(null);
    if (f) parseLocal(f);
    else setPreview(null);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadTemplate = () => {
    const a = document.createElement("a");
    a.href = BULK_SAMPLE_URL;
    a.download = "BDU_Church_Pack_Pastor_List_Template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    if (!preview || preview.valid === 0) {
      toast.error("No valid email rows found. Check the CSV format.");
      return;
    }
    const confirmMsg = `Send the Church Pack to ${preview.valid} pastor${preview.valid === 1 ? "" : "s"}? ` +
      (preview.invalid || preview.duplicate
        ? `(${preview.invalid} invalid and ${preview.duplicate} duplicate rows will be skipped)`
        : "");
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (note.trim()) fd.append("note", note.trim());
      const token = localStorage.getItem("bdu_token");
      const resp = await fetch(
        `${API}/admin/credit-union/outreach/bulk-send-church-pack`,
        { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(formatApiError(data?.detail) || "Bulk send failed");
        return;
      }
      setResult(data);
      toast.success(`Queued ${data.queued} Church Pack email${data.queued === 1 ? "" : "s"}`);
      onComplete?.();
    } catch (err) {
      toast.error(err.message || "Bulk send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mt-6 pt-5 border-t border-bdu-gold/15"
      data-testid="admin-cu-bulk-blast"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border border-bdu-gold/40 bg-bdu-gold/5">
            <Users size={15} className="text-bdu-gold" />
          </div>
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <Upload size={11} className="text-bdu-gold" /> Bulk CSV Blast
            </div>
            <h4 className="mt-1 font-serif text-base text-bdu-cream">
              Upload a list of pastors — send the Pack to all in one click
            </h4>
            <p className="mt-1 text-xs text-bdu-cream/55 max-w-lg leading-relaxed">
              CSV header must include an <code className="text-bdu-gold">email</code>{" "}
              column. Optional columns:{" "}
              <code className="text-bdu-gold">pastor_name</code>,{" "}
              <code className="text-bdu-gold">church_name</code>. Invalid rows
              and duplicates are skipped automatically.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={downloadTemplate}
          className="btn-ghost text-xs flex items-center gap-2"
          data-testid="admin-cu-bulk-template-btn"
        >
          <Download size={13} /> CSV Template
        </button>
      </div>

      <form onSubmit={send} className="mt-5 grid gap-3" data-testid="admin-cu-bulk-form">
        {/* File picker */}
        <label
          className={`flex items-center justify-between gap-3 px-4 py-3 border-2 border-dashed cursor-pointer transition-colors ${
            file ? "border-bdu-gold/50 bg-bdu-gold/5" : "border-bdu-gold/25 hover:border-bdu-gold/50"
          }`}
          data-testid="admin-cu-bulk-dropzone"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Upload size={18} className="text-bdu-gold flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm text-bdu-cream truncate">
                {file ? file.name : "Choose a .csv file…"}
              </div>
              <div className="text-[11px] text-bdu-cream/50">
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB`
                  : "Up to 2 MB · UTF-8 encoded"}
              </div>
            </div>
          </div>
          {file ? (
            <button
              type="button"
              onClick={(ev) => {
                ev.preventDefault();
                clearFile();
              }}
              className="text-xs text-bdu-cream/50 hover:text-bdu-red transition-colors flex items-center gap-1"
              data-testid="admin-cu-bulk-clear-btn"
            >
              <Trash2 size={13} /> Remove
            </button>
          ) : (
            <span className="text-[11px] uppercase tracking-widest text-bdu-gold">
              Browse
            </span>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            className="hidden"
            data-testid="admin-cu-bulk-file-input"
          />
        </label>

        {/* Preview */}
        {preview && (
          <div
            className="grid grid-cols-3 gap-3 text-center"
            data-testid="admin-cu-bulk-preview"
          >
            <PreviewTile label="Will Send" value={preview.valid} good />
            <PreviewTile label="Invalid Email" value={preview.invalid} warn />
            <PreviewTile label="Duplicate" value={preview.duplicate} warn />
          </div>
        )}
        {preview?.missing_header && (
          <div className="flex items-start gap-2 text-xs text-bdu-red bg-bdu-red/10 border border-bdu-red/30 p-3">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>
              This CSV doesn't have an <code>email</code> column header. Please
              match the template.
            </span>
          </div>
        )}

        {/* Shared note */}
        <label className="text-xs text-bdu-cream/60 flex flex-col gap-1">
          Personal note (optional, prepended to every email)
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={800}
            rows={2}
            placeholder="Pastor, following up on our outreach visit this week…"
            disabled={sending}
            data-testid="admin-cu-bulk-note"
            className="bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none transition resize-y disabled:opacity-50"
          />
        </label>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-[11px] text-bdu-cream/40 italic">
            One Church Pack PDF (~25 KB) is attached to every email via Resend.
          </p>
          <button
            type="submit"
            disabled={sending || !preview || preview.valid === 0}
            className="btn-gold text-xs flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-cu-bulk-send-btn"
          >
            {sending ? (
              <>
                <RefreshCw size={14} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send size={14} />
                Blast to {preview?.valid || 0} Pastor{(preview?.valid || 0) === 1 ? "" : "s"}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Result */}
      {result && (
        <div
          className="mt-4 border border-bdu-gold/30 bg-black/40 p-4 text-xs text-bdu-cream/80"
          data-testid="admin-cu-bulk-result"
        >
          <div className="flex items-center gap-2 text-bdu-gold font-semibold uppercase tracking-widest text-[11px]">
            <Mail size={12} /> Blast Queued
          </div>
          <div className="mt-2 grid sm:grid-cols-4 gap-3">
            <ResultCell label="Queued" value={result.queued} />
            <ResultCell label="Invalid" value={result.skipped_invalid} />
            <ResultCell label="Duplicate" value={result.skipped_duplicate} />
            <ResultCell
              label="PDF Size"
              value={`${Math.round((result.attachment_bytes || 0) / 1024)} KB`}
            />
          </div>
          <div className="mt-3 text-[11px] text-bdu-cream/50 italic">
            Source file: {result.source_csv}
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewTile({ label, value, good, warn }) {
  const palette = good
    ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300"
    : warn && value > 0
    ? "border-amber-500/40 bg-amber-500/5 text-amber-300"
    : "border-bdu-gold/15 bg-black/30 text-bdu-cream/60";
  return (
    <div className={`border p-2.5 ${palette}`}>
      <div className="text-[9px] uppercase tracking-[0.22em] opacity-80">{label}</div>
      <div className="mt-1 font-serif text-2xl">{value}</div>
    </div>
  );
}

function ResultCell({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-widest text-bdu-cream/50">
        {label}
      </span>
      <span className="font-serif text-xl text-bdu-cream">{value}</span>
    </div>
  );
}



// ===========================================================
// FOMPacketCard — one-click NCUA submission bundle download
// ===========================================================
function FOMPacketCard() {
  const [downloading, setDownloading] = useState(null); // "packet" | "study" | "stats" | "csv" | "map-branded" | "map-formal"

  const downloadBlob = async (kind, path, filename) => {
    setDownloading(kind);
    try {
      const token = localStorage.getItem("bdu_token");
      const r = await fetch(`${API}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const ts = () => new Date().toISOString().slice(0, 10).replace(/-/g, "");

  return (
    <div
      className="mt-6 border border-bdu-gold/30 bg-black/40 p-6"
      data-testid="admin-cu-fom-packet-card"
    >
      <div className="flex items-start justify-between flex-wrap gap-6">
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow flex items-center gap-2">
            <FileText size={12} className="text-bdu-gold" /> NCUA Form 9500 · FOM Packet
          </div>
          <h3 className="mt-3 font-serif text-2xl text-bdu-cream">
            Download the complete Field-of-Membership submission bundle
          </h3>
          <p className="mt-2 text-sm text-bdu-cream/60 leading-relaxed max-w-2xl">
            One-click ZIP contains the full FOM Study PDF, live potential-member
            statistics, both branded and NCUA-formal boundary maps, the ZIP
            registry CSV, and a submission README. Scope: Fulton County +
            DeKalb County, Georgia.
          </p>
        </div>
        <button
          type="button"
          onClick={() =>
            downloadBlob(
              "packet",
              "/admin/credit-union/fom/packet.zip",
              `BDU_Proposed_FOM_Packet_${ts()}.zip`,
            )
          }
          disabled={downloading === "packet"}
          className="btn-gold text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="admin-cu-fom-packet-btn"
        >
          <Download size={14} />
          {downloading === "packet" ? "Preparing…" : "Download Full NCUA Packet (ZIP)"}
        </button>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="admin-cu-fom-packet-parts">
        <FOMPartBtn
          testId="admin-cu-fom-study-btn"
          label="FOM Study"
          sub="Narrative + map + ZIPs"
          onClick={() =>
            downloadBlob(
              "study",
              "/admin/credit-union/fom/study.pdf",
              `BDU_Proposed_FOM_Study_${ts()}.pdf`,
            )
          }
          busy={downloading === "study"}
        />
        <FOMPartBtn
          testId="admin-cu-fom-stats-btn"
          label="Member Stats"
          sub="Live supporter numbers"
          onClick={() =>
            downloadBlob(
              "stats",
              "/admin/credit-union/fom/stats.pdf",
              `BDU_Proposed_FOM_Stats_${ts()}.pdf`,
            )
          }
          busy={downloading === "stats"}
        />
        <FOMPartBtn
          testId="admin-cu-fom-csv-btn"
          label="ZIP List CSV"
          sub="Flat registry (Fulton + DeKalb)"
          onClick={() =>
            downloadBlob(
              "csv",
              "/admin/credit-union/fom/zips.csv",
              `BDU_Proposed_FOM_ZIPs_${ts()}.csv`,
            )
          }
          busy={downloading === "csv"}
        />
        <FOMPartBtn
          testId="admin-cu-fom-map-formal-btn"
          label="Map · Formal"
          sub="NCUA-ready PNG"
          onClick={() =>
            downloadBlob(
              "map-formal",
              "/credit-union/fom/snapshot.png?style=formal",
              `BDU_Proposed_FOM_Map_Formal_${ts()}.png`,
            )
          }
          busy={downloading === "map-formal"}
        />
        <FOMPartBtn
          testId="admin-cu-fom-map-branded-btn"
          label="Map · Branded"
          sub="Marketing PNG"
          onClick={() =>
            downloadBlob(
              "map-branded",
              "/credit-union/fom/snapshot.png?style=branded",
              `BDU_Proposed_FOM_Map_Branded_${ts()}.png`,
            )
          }
          busy={downloading === "map-branded"}
        />
      </div>

      <p className="mt-4 text-[11px] text-bdu-cream/40 italic leading-relaxed">
        Compliance note. The FOM Study PDF includes the WDLC narrative, boundary
        map, service-area statistics, and the complete ZIP code registry — the
        four artifacts the NCUA Office of Credit Union Resources & Expansion
        asks for alongside Form 9500 under Chapter 2, Appendix B.
      </p>
    </div>
  );
}

function FOMPartBtn({ label, sub, onClick, busy, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className="group text-left border border-bdu-gold/20 bg-bdu-surface2 hover:border-bdu-gold hover:bg-bdu-gold/5 transition p-3 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-bdu-gold">{label}</div>
        <Download size={12} className="text-bdu-cream/50 group-hover:text-bdu-gold transition" />
      </div>
      <div className="mt-1.5 text-xs text-bdu-cream/70 leading-snug">
        {busy ? "Downloading…" : sub}
      </div>
    </button>
  );
}


// ===========================================================
// FormsVaultCard — Charter Forms Vault
// 3 BDU-authored fillable templates we host + 6 NCUA forms we link out to.
// ===========================================================

const BDU_FORM_ICONS = {
  "sponsor-commitment": FileSignature,
  "financial-needs-survey": ClipboardList,
  "membership-pledge": HandCoins,
};

function FormsVaultCard() {
  const [index, setIndex] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [surveyOpen, setSurveyOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/credit-union/forms/index");
        if (!cancelled) setIndex(r.data);
      } catch (e) {
        if (!cancelled) setLoadErr(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const downloadBlob = async (kind, path, filename) => {
    setDownloading(kind);
    try {
      const token = localStorage.getItem("bdu_token");
      const r = await fetch(`${API}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const ts = () => new Date().toISOString().slice(0, 10).replace(/-/g, "");

  return (
    <div
      className="mt-6 border border-bdu-gold/30 bg-black/40 p-6"
      data-testid="admin-cu-forms-vault-card"
    >
      <div className="flex items-start justify-between flex-wrap gap-6">
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow flex items-center gap-2">
            <FileText size={12} className="text-bdu-gold" /> Charter Forms Vault
          </div>
          <h3 className="mt-3 font-serif text-2xl text-bdu-cream">
            One home for every form the board & organizing committee needs.
          </h3>
          <p className="mt-2 text-sm text-bdu-cream/60 leading-relaxed max-w-2xl">
            Three fillable templates authored by Black Dollars United for the
            TDSPFCU chartering effort, plus direct links to the six official
            NCUA forms maintained on ncua.gov. Download the full board kit as
            one archive or grab each form individually.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setSurveyOpen(true)}
            className="inline-flex items-center gap-2 border border-bdu-gold/50 text-bdu-cream hover:bg-bdu-gold/10 px-4 py-2.5 text-xs tracking-[0.2em] uppercase transition"
            data-testid="admin-cu-forms-record-survey-btn"
          >
            <Plus size={14} /> Record Paper Survey
          </button>
          <button
            type="button"
            onClick={() =>
              downloadBlob(
                "kit",
                "/admin/credit-union/forms/kit.zip",
                `BDU_TDSPFCU_Charter_Forms_Kit_${ts()}.zip`,
              )
            }
            disabled={downloading === "kit"}
            className="btn-gold text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-cu-forms-kit-btn"
          >
            <Download size={14} />
            {downloading === "kit" ? "Preparing…" : "Download Board Kit (ZIP)"}
          </button>
        </div>
      </div>

      <SurveyIntakeModal open={surveyOpen} onClose={() => setSurveyOpen(false)} />

      {loadErr && (
        <div
          className="mt-5 border border-amber-500/50 bg-amber-500/5 text-amber-200 text-sm p-3"
          data-testid="admin-cu-forms-error"
        >
          Could not load forms index — {loadErr}
        </div>
      )}

      {index && (
        <>
          {/* BDU-authored templates */}
          <div className="mt-6" data-testid="admin-cu-forms-bdu-group">
            <div className="label-eyebrow mb-3">BDU-Authored · Fillable Templates</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {index.bdu_authored.map((f) => {
                const Icon = BDU_FORM_ICONS[f.slug] || FileText;
                const key = `bdu-${f.slug}`;
                const busy = downloading === key;
                return (
                  <div
                    key={f.slug}
                    className="border border-bdu-gold/20 bg-bdu-surface2 p-4 flex flex-col"
                    data-testid={`admin-cu-forms-bdu-${f.slug}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 border border-bdu-gold/40 flex items-center justify-center shrink-0">
                        <Icon size={16} className="text-bdu-gold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif text-base text-bdu-cream leading-tight">
                          {f.title}
                        </div>
                        <p className="mt-1.5 text-xs text-bdu-cream/60 leading-relaxed">
                          {f.purpose}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        downloadBlob(
                          key,
                          f.download,
                          `BDU_${f.slug.replace(/-/g, "_")}_${ts()}.pdf`,
                        )
                      }
                      disabled={busy}
                      className="mt-4 inline-flex items-center justify-center gap-2 border border-bdu-gold/40 hover:border-bdu-gold hover:bg-bdu-gold/10 text-bdu-cream text-[11px] tracking-[0.22em] uppercase py-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`admin-cu-forms-bdu-${f.slug}-btn`}
                    >
                      <Download size={12} />
                      {busy ? "Downloading…" : "Download PDF"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* NCUA official forms — linked to ncua.gov */}
          <div className="mt-8" data-testid="admin-cu-forms-ncua-group">
            <div className="label-eyebrow mb-3 flex items-center gap-2">
              NCUA Official · Download from NCUA.gov
              <span className="text-[9px] tracking-[0.18em] text-bdu-cream/40 border border-bdu-cream/20 px-1.5 py-[1px]">
                EXTERNAL
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {index.ncua_official.map((f) => (
                <div
                  key={f.code}
                  className="border border-bdu-cream/15 bg-bdu-surface2 p-4 flex flex-col"
                  data-testid={`admin-cu-forms-ncua-${f.code}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-bdu-gold">
                        NCUA Form {f.code}
                      </div>
                      <div className="mt-1 font-serif text-sm text-bdu-cream leading-snug">
                        {f.title}
                      </div>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-bdu-cream/55 leading-relaxed flex-1">
                    {f.purpose}
                  </p>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center justify-center gap-2 border border-bdu-cream/25 hover:border-bdu-gold hover:text-bdu-gold text-bdu-cream text-[11px] tracking-[0.22em] uppercase py-2 transition"
                    data-testid={`admin-cu-forms-ncua-${f.code}-btn`}
                  >
                    <ExternalLink size={12} />
                    Open on NCUA.gov
                  </a>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <p className="mt-6 text-[11px] text-bdu-cream/40 italic leading-relaxed">
        Compliance note. BDU-authored templates are provided for the proposed
        chartering effort of the Proposed Trillion Dollar Spending Power
        Federal Credit Union. The six official NCUA forms are not re-hosted
        here — always download the current revision from ncua.gov to avoid
        working from a superseded version.
      </p>
    </div>
  );
}

// ===========================================================
// Documented Demand tiles — paper + inline survey tally vs NCUA benchmark
// ===========================================================

// Tiny module-level pub/sub so the modal in FormsVaultCard can tell the
// tiles to refresh after a new survey is recorded, without lifting state.
const _surveyBus = {
  _listeners: new Set(),
  subscribe(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  },
  publish() {
    this._listeners.forEach((fn) => {
      try { fn(); } catch (e) { /* ignore */ }
    });
  },
};

function _formatNumber(n) {
  const v = Number(n || 0);
  return Number.isFinite(v) ? v.toLocaleString() : "0";
}

function _nextBenchmark(tally) {
  const inFom = Number(tally?.in_fom || 0);
  const bm = tally?.benchmarks || {};
  const tiers = [
    { label: "1% Floor",    needed: Number(bm.floor_1pct || 0) },
    { label: "2.5% Target", needed: Number(bm.target_2_5pct || 0) },
    { label: "5% Strong",   needed: Number(bm.strong_5pct || 0) },
  ];
  for (const t of tiers) {
    if (t.needed > 0 && inFom < t.needed) {
      return { ...t, remaining: t.needed - inFom, hit: false };
    }
  }
  return { label: "5% Strong", needed: tiers[2].needed, remaining: 0, hit: true };
}

function DocumentedDemandTiles() {
  const [tally, setTally] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/credit-union/surveys/tally");
      setTally(r.data);
      setErr(null);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    const unsub = _surveyBus.subscribe(load);
    return () => unsub();
  }, [load]);

  const next = tally ? _nextBenchmark(tally) : null;
  const pct = tally?.pct_of_population || 0;

  return (
    <div className="mt-6" data-testid="admin-cu-demand">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div className="label-eyebrow flex items-center gap-2">
          <BarChart3 size={12} className="text-bdu-gold" />
          Documented Demand · NCUA Evidence Benchmark
        </div>
        <button
          type="button"
          onClick={load}
          className="btn-ghost text-[10px] flex items-center gap-1.5"
          data-testid="admin-cu-demand-refresh"
          title="Refresh tally"
        >
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {err && (
        <div
          className="mb-3 border border-amber-500/40 bg-amber-500/5 text-amber-200 text-xs p-2"
          data-testid="admin-cu-demand-error"
        >
          Could not load tally — {err}
        </div>
      )}

      <div className="grid sm:grid-cols-4 gap-4">
        <StatTile
          label="Surveys Collected"
          value={_formatNumber(tally?.total)}
          sub={`${_formatNumber(tally?.in_fom)} inside FOM · ${_formatNumber(tally?.out_fom)} outside`}
          testId="admin-cu-demand-total"
          gold
        />
        <StatTile
          label="Share of FOM Population"
          value={`${pct.toFixed(3)}%`}
          sub={`of ${_formatNumber(tally?.fom_population)} residents`}
          testId="admin-cu-demand-pct"
        />
        <StatTile
          label={next?.hit ? "5% Strong — met" : `To reach ${next?.label || "1% Floor"}`}
          value={next?.hit ? "✓" : _formatNumber(next?.remaining)}
          sub={next?.hit
            ? "Strong signal of community demand"
            : `${_formatNumber(next?.needed)} needed · ${_formatNumber(next?.remaining)} to go`}
          testId="admin-cu-demand-next"
          gold={!!next?.hit}
        />
        <StatTile
          label="By County"
          value={`${_formatNumber(tally?.by_county?.Fulton)} / ${_formatNumber(tally?.by_county?.DeKalb)}`}
          sub={`Fulton / DeKalb · ${_formatNumber(tally?.by_county?.["Out of FOM"])} out-of-FOM`}
          testId="admin-cu-demand-by-county"
        />
      </div>
    </div>
  );
}


// ===========================================================
// Survey Intake Modal
// ===========================================================

const _LIKELIHOOD_OPTIONS = [
  { v: "absolutely",  label: "Absolutely — day one applicant" },
  { v: "very_likely", label: "Very likely" },
  { v: "likely",      label: "Likely — pending the terms" },
  { v: "possibly",    label: "Possibly — needs more info" },
  { v: "unlikely",    label: "Unlikely" },
];
const _SOURCE_OPTIONS = [
  { v: "paper",   label: "Paper form (default)" },
  { v: "church",  label: "Church / faith event" },
  { v: "rally",   label: "Rally of the Minds" },
  { v: "event",   label: "Community event" },
  { v: "canvass", label: "Door-to-door canvass" },
  { v: "phone",   label: "Phone call" },
  { v: "digital", label: "Digitized from scan" },
];
const _AGE_OPTIONS = [
  { v: "",      label: "— unspecified —" },
  { v: "18-24", label: "18 – 24" },
  { v: "25-34", label: "25 – 34" },
  { v: "35-44", label: "35 – 44" },
  { v: "45-54", label: "45 – 54" },
  { v: "55-64", label: "55 – 64" },
  { v: "65+",   label: "65 and over" },
];

function SurveyIntakeModal({ open, onClose }) {
  const [zip, setZip] = useState("");
  const [likely, setLikely] = useState("absolutely");
  const [age, setAge] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [source, setSource] = useState("paper");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setZip(""); setLikely("absolutely"); setAge("");
      setName(""); setEmail(""); setSource("paper"); setNotes("");
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    const clean = (zip || "").replace(/\D+/g, "").slice(0, 5);
    if (clean.length !== 5) {
      toast.error("Enter a valid 5-digit ZIP code");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/credit-union/surveys/intake", {
        zip_code: clean,
        likely_to_join: likely,
        age_range: age || null,
        respondent_name: name.trim() || null,
        email: email.trim() || null,
        source,
        notes: notes.trim() || null,
      });
      const county = r.data?.fom_county;
      const inFom = r.data?.in_fom;
      toast.success(
        inFom
          ? `Recorded · ${county} County · in FOM`
          : `Recorded · ZIP ${clean} is outside the FOM`,
      );
      _surveyBus.publish();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 sm:p-10 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-cu-survey-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="relative max-w-xl w-full bg-bdu-bg border border-bdu-gold/40 p-6 sm:p-8 shadow-[0_0_60px_rgba(212,175,55,0.12)]"
        data-testid="admin-cu-survey-modal-form"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-bdu-cream/60 hover:text-bdu-cream"
          data-testid="admin-cu-survey-modal-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="label-eyebrow flex items-center gap-2">
          <ClipboardList size={12} className="text-bdu-gold" /> Record Paper Survey
        </div>
        <h3 className="mt-3 font-serif text-2xl text-bdu-cream">
          Quick entry · one record per completed survey
        </h3>
        <p className="mt-2 text-xs text-bdu-cream/55 leading-relaxed">
          Just the demand-critical fields. The record counts toward the NCUA
          Evidence of Demand benchmark; inside / outside-FOM is derived from
          the ZIP automatically.
        </p>

        <div className="mt-5 space-y-4">
          <Field label="ZIP code *" testId="admin-cu-survey-zip">
            <input
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              inputMode="numeric"
              maxLength={5}
              required
              placeholder="30309"
              className="w-full bg-bdu-surface2 border border-bdu-gold/25 focus:border-bdu-gold outline-none text-bdu-cream px-3 py-2 text-sm"
              data-testid="admin-cu-survey-zip-input"
            />
          </Field>

          <Field label="Likelihood to join *" testId="admin-cu-survey-likelihood">
            <select
              value={likely}
              onChange={(e) => setLikely(e.target.value)}
              className="w-full bg-bdu-surface2 border border-bdu-gold/25 focus:border-bdu-gold outline-none text-bdu-cream px-3 py-2 text-sm"
              data-testid="admin-cu-survey-likelihood-input"
            >
              {_LIKELIHOOD_OPTIONS.map((o) => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Collected via" testId="admin-cu-survey-source">
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full bg-bdu-surface2 border border-bdu-gold/25 focus:border-bdu-gold outline-none text-bdu-cream px-3 py-2 text-sm"
                data-testid="admin-cu-survey-source-input"
              >
                {_SOURCE_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Age range (optional)" testId="admin-cu-survey-age">
              <select
                value={age}
                onChange={(e) => setAge(e.target.value)}
                className="w-full bg-bdu-surface2 border border-bdu-gold/25 focus:border-bdu-gold outline-none text-bdu-cream px-3 py-2 text-sm"
                data-testid="admin-cu-survey-age-input"
              >
                {_AGE_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>{o.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Respondent name (optional)" testId="admin-cu-survey-name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder="Jane Doe"
              className="w-full bg-bdu-surface2 border border-bdu-gold/25 focus:border-bdu-gold outline-none text-bdu-cream px-3 py-2 text-sm"
              data-testid="admin-cu-survey-name-input"
            />
          </Field>

          <Field label="Email (optional, for progress updates)" testId="admin-cu-survey-email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={240}
              placeholder="jane@example.org"
              className="w-full bg-bdu-surface2 border border-bdu-gold/25 focus:border-bdu-gold outline-none text-bdu-cream px-3 py-2 text-sm"
              data-testid="admin-cu-survey-email-input"
            />
          </Field>

          <Field label="Notes (optional)" testId="admin-cu-survey-notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={600}
              rows={2}
              placeholder="Where / event / anything to remember"
              className="w-full bg-bdu-surface2 border border-bdu-gold/25 focus:border-bdu-gold outline-none text-bdu-cream px-3 py-2 text-sm resize-none"
              data-testid="admin-cu-survey-notes-input"
            />
          </Field>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost text-xs"
            data-testid="admin-cu-survey-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="btn-gold text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-cu-survey-submit"
          >
            <Plus size={14} />
            {busy ? "Recording…" : "Record Survey"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-bdu-cream/60 mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}

